import type { IndividualApi } from '@banking/ui/workflows/onboarding-ci/update';
import {
  fromSfsApiControllingIndividual,
  Individual,
} from '@banking/api/models/onboarding/individual';
import { ApplicationFlowUpdate } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow_update';
import { ApplicationFlow } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow';
import { Ssn } from '@square/dashboard-protos/models/squareup/sfs/external/models/controlling_individual';
import { ConnectApiDate } from '@banking/api/utils/date-format';
import type CurrentApplicationFlowService from '@banking/api/services/current-application-flow';

export default class SFSIndividualApi implements IndividualApi {
  applicationFlowId: string;
  errorHandler: (error: unknown) => void;
  currentApplicationFlow: CurrentApplicationFlowService;

  constructor(
    applicationFlowId: string,
    currentApplicationFlow: CurrentApplicationFlowService,
    errorHandler: (error: unknown) => void
  ) {
    this.applicationFlowId = applicationFlowId;
    this.currentApplicationFlow = currentApplicationFlow;
    this.errorHandler = errorHandler;
  }

  edit(individual: Individual): Promise<readonly Individual[]> {
    let controllingIndividualUpdate =
      new ApplicationFlowUpdate.ControllingIndividualUpdate.Builder()
        .id(individual.id)
        .isManager(individual.isManager)
        .hasSignificantOwnership(individual.isSignificantOwner)
        .givenName(individual.givenName)
        .surname(individual.surname)
        .address(individual.address)
        .birthDate(ConnectApiDate.format(individual.birthDate));

    if (
      individual.governmentId.value &&
      individual.governmentId.value.length > 0
    ) {
      const ssn = new Ssn.Builder()
        .value(individual.governmentId.value)
        .build();
      controllingIndividualUpdate = controllingIndividualUpdate.ssn(ssn);
    }

    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.applicationFlowId)
      .updateControllingIndividuals([controllingIndividualUpdate.build()])
      .build();

    return this._performUpdate(applicationFlowUpdate);
  }

  add(individual: Individual): Promise<readonly Individual[]> {
    const ssn = new Ssn.Builder().value(individual.governmentId.value).build();

    const controllingIndividualAdd =
      new ApplicationFlowUpdate.ControllingIndividualAdd.Builder()
        .isManager(individual.isManager)
        .hasSignificantOwnership(individual.isSignificantOwner)
        .givenName(individual.givenName)
        .surname(individual.surname)
        .address(individual.address)
        .birthDate(ConnectApiDate.format(individual.birthDate))
        .ssn(ssn)
        .build();

    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.applicationFlowId)
      .addControllingIndividuals([controllingIndividualAdd])
      .build();

    const response = this._performUpdate(applicationFlowUpdate);
    return response;
  }

  delete(individualId: string): Promise<readonly Individual[]> {
    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.applicationFlowId)
      .removeControllingIndividualIds([individualId])
      .build();

    return this._performUpdate(applicationFlowUpdate);
  }

  async _performUpdate(
    update: ApplicationFlowUpdate
  ): Promise<readonly Individual[]> {
    let updatedApplicationFlow: ApplicationFlow | undefined;
    try {
      updatedApplicationFlow = await this.currentApplicationFlow.update(update);
    } catch (error) {
      this.errorHandler(error);
    }

    if (updatedApplicationFlow) {
      return updatedApplicationFlow.controllingIndividuals.map(
        fromSfsApiControllingIndividual
      );
    } else {
      throw new Error('no updated application flow returned');
    }
  }
}
